import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API_URL } from "../../config.js";
import Notification from "../../components/Notification.js";
import FarmersDropdownField from "../../components/FarmersDropdownField.js";
import { getLocalDate } from "../../utils/TextUtils.js";

function MilkCollectionForm() {
    const [milkCollection, setMilkCollection] = useState({
        farmer_id: "",
        collection_date: "",
        litres: "",
        collection_shift: "", 
    });

    const [loading, setLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [isEditing, setIsEditing] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams();
    const token = localStorage.getItem("access_token");
    const [allFarmers, setAllFarmers] = useState([]);

    useEffect(() => {
        const fetchMilkCollection = async () => {
            if (id) {
                try {
                    const response = await fetch(`${API_URL}/milk-collections/${id}`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    });
                    if (response.ok) {
                        const data = await response.json();
                        setMilkCollection(data);
                        setIsEditing(true);
                    } else if (response.status === 404) {
                        setNotification({ type: "danger", message: "Milk collection not found." });
                    } else if (response.status === 401) {
                        navigate("/logout");
                    } else {
                        throw new Error("Failed to fetch milk collection.");
                    }
                } catch (error) {
                    setNotification({ type: "danger", message: "An error occurred while fetching the milk collection." });
                }
            }
        };

        const fetchAllFarmers = async () => {
            try {
                const response = await fetch(`${API_URL}/farmers?paginate=false`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    setAllFarmers(data);
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: "Farmers not found." });
                } else if (response.status === 401) {
                    navigate("/logout");
                } else {
                    throw new Error("Failed to fetch farmers.");
                }
            } catch (error) {
                setNotification({ type: "danger", message: "An error occurred while fetching farmers." });
            }
        };

        const fetchData = async () => {
            await fetchMilkCollection();
            await fetchAllFarmers();
            setLoading(false);
        };

        fetchData();
    }, [id, navigate, token]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setMilkCollection((prevMilkCollection) => ({
            ...prevMilkCollection,
            [name]: value,
        }));
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});

        try {
            const method = "POST";
            const url = isEditing ? `${API_URL}/milk-collections/${id}/update` : `${API_URL}/milk-collections`;

            const formData = new FormData();

            for (const key in milkCollection) {
                if (milkCollection[key] === null || milkCollection[key] === "") {
                    formData.delete(key);
                } else {
                    formData.append(key, milkCollection[key]);
                }
            }

            setLoading(true);
            const response = await fetch(url, {
                method,
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            if (response.ok) {
                setNotification({ message: "Milk collection successfully saved.", type: "success" });
                navigate(`/dashboard/milk-collections`);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                try {
                    const errorData = await response.json();
                    setErrors(errorData.errors || {});
                    setNotification({ message: errorData.message || "An error occurred.", type: "danger" });
                } catch (jsonError) {
                    console.error("Failed to parse error JSON:", jsonError);
                    setNotification({ message: `Unexpected error: ${response.statusText}`, type: "danger" });
                }
            }
        } catch (error) {
            setNotification({ message: "An error occurred while saving the milk collection.", type: "danger" });
        } finally {
            setLoading(false);
        }
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    if (loading) return <p>Loading...</p>;

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">
                            {isEditing ? "Update Milk Collection" : "Add Milk Collection"}
                        </p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/milk-collections" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back
                        </Link>
                    </p>
                </div>
            </nav>
            <div className="column dash-section is-loading">
                {notification.message && (
                    <Notification
                        type={notification.type}
                        message={notification.message}
                        onClose={closeNotification}
                    />
                )}

                <form onSubmit={handleSubmit}>
                    <div className="columns">
                        <div className="column is-half">
                            {/* Farmer Dropdown */}
                            <div className="field">
                                <div className="control">
                                    <FarmersDropdownField
                                        label="Farmer"
                                        name="farmer_id"
                                        value={milkCollection.farmer_id}
                                        onChange={handleChange}
                                        options={allFarmers}
                                        errors={errors.farmer_id}
                                        placeholder="Select a farmer"
                                    />
                                </div>
                                {errors.farmer_id && errors.farmer_id.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Collection Date Field */}
                            <div className="field">
                                <label className="label">Collection Date</label>
                                <div className="control">
                                    <input
                                        className={`input ${errors.collection_date ? "is-danger" : ""}`}
                                        type="date"
                                        name="collection_date"
                                        value={milkCollection.collection_date}
                                        onChange={handleChange}
                                        required
                                        max={ getLocalDate()}
                                    />
                                </div>
                                {errors.collection_date && errors.collection_date.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Collection Time Field */}
                            <div className="field">
                                <label className="label">Collection Time</label>
                                <div className="control">
                                    <div className={`select  is-fullwidth ${errors.collection_shift ? "is-danger" : ""}`}>
                                        <select
                                            name="collection_shift"
                                            value={milkCollection.collection_shift}
                                            onChange={handleChange}
                                            required
                                        >
                                             <option value="">Select an option</option>
                                            <option value="Morning">Morning</option>
                                            <option value="Evening">Evening</option>
                                        </select>
                                    </div>
                                </div>
                                {errors.collection_shift && errors.collection_shift.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            {/* Litres Field */}
                            <div className="field">
                                <label className="label">Litres</label>
                                <div className="control">
                                    <input
                                        className={`input ${errors.litres ? "is-danger" : ""}`}
                                        type="number"
                                        step="0.1"
                                        name="litres"
                                        value={milkCollection.litres}
                                        onChange={handleChange}
                                        placeholder="Litres"
                                        required
                                    />
                                </div>
                                {errors.litres && errors.litres.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="columns">
                        <div className="column is-half">
                            <div className="field">
                                <div className="control">
                                    <button type="submit" className="button primary-bg is-pulled-right is-fullwidth" disabled={loading}>
                                        {isEditing ? "UPDATE MILK COLLECTION" : "POST MILK COLLECTION"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </main>
    );
}

export default MilkCollectionForm;